import type { EnrichmentData } from '../components/EnrichmentProfileCard';
import { getLeadPlatforms, type LeadPlatform } from './leadPlatforms';

/**
 * The enrichment a lead has on a network OTHER than the one its own fields hold.
 *
 * `Lead.customFields` carries one flat set of enrichment fields — one `score`,
 * one `aiAnalysis` — and the orchestrator only writes them when the scraped
 * profile lands on the primary contact. Enrich Instagram first and LinkedIn
 * second and the LinkedIn analysis goes to the contact row alone, so the screen
 * showed the Instagram enrichment and nothing else. The data was there; nothing
 * read it.
 */

interface ContactLike {
  sourceUrl?: string | null;
  customFields?: Record<string, unknown> | null;
}

interface EnrichmentSource {
  sourceUrl?: string | null;
  contacts?: ContactLike[] | null;
}

export interface ContactEnrichment {
  platform: LeadPlatform;
  data: EnrichmentData;
}

/**
 * Everything the other network concluded, verdict included.
 *
 * An explicit allow-list rather than `ENRICHMENT_FIELD_KEYS` minus a deny-list:
 * a key added to that constant later would otherwise leak in unnoticed, and this
 * module should not depend on a UI component's constant to decide what it reads.
 *
 * The scores were withheld for as long as nothing recorded WHAT each one
 * measured. Two bare gauges side by side invited a comparison that means nothing
 * — a LinkedIn 8 is purchase potential, an Instagram 8 on a personal account is
 * how clearly the interests could be read. `scoreAxis` now records the question
 * each answered, and `ScoreGauge` already labels its three variants distinctly,
 * so each network can state its own verdict without being read as a ranking.
 */
const SURFACED_KEYS = [
  'score',
  'scoreAxis',
  'reasonScore',
  'economicCapacity',
  'brandScore',
  'brandCapacity',
  'interestScore',
  'engagementLevel',
  'company',
  'headline',
  'industry',
  'location',
  'username',
  'followerCount',
  'capturedContent',
  'enrichedAt',
  'enrichmentMethod',
  'aiAnalysis',
  'painPoints',
  'profileKind',
  'brandFocus',
  'contentType',
  'brandDescription',
  'interestsSummary',
  'interestTopics',
] as const satisfies ReadonlyArray<keyof EnrichmentData>;

function platformOf(url: unknown): LeadPlatform | undefined {
  return typeof url === 'string' ? getLeadPlatforms({ sourceUrl: url }).at(0) : undefined;
}

function enrichmentOf(customFields: Record<string, unknown> | null | undefined) {
  if (!customFields) return undefined;

  const entries = SURFACED_KEYS.filter(
    (key) => customFields[key] != null && customFields[key] !== '',
  ).map((key) => [key, customFields[key]]);

  return entries.length > 0 ? (Object.fromEntries(entries) as EnrichmentData) : undefined;
}

/**
 * One entry per network whose enrichment the lead's own fields do not already
 * show, newest contact last. Empty when every enriched network is already
 * represented at lead level — the common case, and the one that must not grow a
 * duplicate card.
 */
export function collectContactEnrichments(lead: EnrichmentSource): ContactEnrichment[] {
  const covered = new Set<LeadPlatform>();
  const leadPlatform = platformOf(lead.sourceUrl);
  if (leadPlatform) covered.add(leadPlatform);

  const found: ContactEnrichment[] = [];

  for (const contact of lead.contacts ?? []) {
    const platform = platformOf(contact?.sourceUrl);
    if (!platform || covered.has(platform)) continue;

    const data = enrichmentOf(contact?.customFields);
    if (!data) continue;

    covered.add(platform);
    found.push({ platform, data });
  }

  return found;
}
